import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "./UserPanel/Sidebar";
const EMPTY_FORM = {
  id: null,
  name: "",
  description: "",
  is_active: true,
};
function getApiMessage(data, fallback) {
  if (typeof data?.message === "string") {
    return data.message;
  }
  if (typeof data?.detail === "string") {
    return data.detail;
  }
  if (data?.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat().join(" ");
  }
  return fallback;
}
export default function AdminRoles() {
  const { fetchUser } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formOpen, setFormOpen] = useState(false);
  const [detailGroup, setDetailGroup] = useState(null);
  const [deleteGroup, setDeleteGroup] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [feedback, setFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const showFeedback = useCallback((severity, message) => {
    setFeedback({
      open: true,
      severity,
      message,
    });
  }, []);
  const readJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return {};
    }
  };
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [organizationResponse, groupsResponse] = await Promise.all([
        fetchUser("/tenants/api/logo/", {
          method: "GET",
          cache: "no-store",
        }),
        fetchUser("/access-control/api/groups/", {
          method: "GET",
          cache: "no-store",
        }),
      ]);
      const organizationData = await readJson(organizationResponse);
      const groupsData = await readJson(groupsResponse);
      if (
        !organizationResponse.ok ||
        organizationData.ok !== true ||
        !organizationData.tenant
      ) {
        throw new Error(
          getApiMessage(
            organizationData,
            "No se pudo cargar la organización."
          )
        );
      }
      if (!groupsResponse.ok || groupsData.ok !== true) {
        throw new Error(
          getApiMessage(groupsData, "No se pudieron cargar los roles.")
        );
      }
      setOrganization(organizationData.tenant);
      setGroups(Array.isArray(groupsData.groups) ? groupsData.groups : []);
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo conectar con el servidor."
      );
    } finally {
      setLoading(false);
    }
  }, [fetchUser, showFeedback]);
  useEffect(() => {
    loadData();
  }, [loadData]);
  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return groups;
    }
    return groups.filter((group) => {
      return (
        String(group.name || "").toLowerCase().includes(query) ||
        String(group.description || "").toLowerCase().includes(query)
      );
    });
  }, [groups, search]);
  const paginatedGroups = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredGroups.slice(start, start + rowsPerPage);
  }, [filteredGroups, page, rowsPerPage]);
  useEffect(() => {
    const lastPage = Math.max(
      0,
      Math.ceil(filteredGroups.length / rowsPerPage) - 1
    );
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [filteredGroups.length, page, rowsPerPage]);
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };
  const openEdit = (group) => {
    setForm({
      id: group.id,
      name: group.name || "",
      description: group.description || "",
      is_active: group.is_active === true,
    });
    setFormOpen(true);
  };
  const closeForm = () => {
    if (saving) {
      return;
    }
    setFormOpen(false);
    setForm(EMPTY_FORM);
  };
  const handleSave = async () => {
    if (!form.name.trim()) {
      showFeedback("error", "El nombre del rol es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      const editing = Boolean(form.id);
      const response = await fetchUser(
        editing
          ? `/access-control/api/groups/${form.id}/`
          : "/access-control/api/groups/",
        {
          method: editing ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: form.name.trim(),
            description: form.description.trim(),
            is_active: form.is_active,
          }),
        }
      );
      const data = await readJson(response);
      if (!response.ok || data.ok !== true) {
        throw new Error(
          getApiMessage(
            data,
            editing
              ? "No se pudo actualizar el rol."
              : "No se pudo crear el rol."
          )
        );
      }
      setFormOpen(false);
      setForm(EMPTY_FORM);
      showFeedback(
        "success",
        data.message ||
          (editing
            ? "Rol actualizado correctamente."
            : "Rol creado correctamente.")
      );
      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo guardar el rol."
      );
    } finally {
      setSaving(false);
    }
  };
  const handleToggleStatus = async (group) => {
    setUpdatingId(group.id);
    try {
      const response = await fetchUser(
        `/access-control/api/groups/${group.id}/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: group.name,
            description: group.description || "",
            is_active: !group.is_active,
          }),
        }
      );
      const data = await readJson(response);
      if (!response.ok || data.ok !== true) {
        throw new Error(
          getApiMessage(data, "No se pudo cambiar el estado del rol.")
        );
      }
      showFeedback(
        "success",
        data.message || "Estado actualizado correctamente."
      );
      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo cambiar el estado del rol."
      );
    } finally {
      setUpdatingId(null);
    }
  };
  const handleDelete = async () => {
    if (!deleteGroup) {
      return;
    }
    setDeletingId(deleteGroup.id);
    try {
      const response = await fetchUser(
        `/access-control/api/groups/${deleteGroup.id}/`,
        {
          method: "DELETE",
        }
      );
      const data = await readJson(response);
      if (!response.ok || data.ok === false) {
        throw new Error(getApiMessage(data, "No se pudo eliminar el rol."));
      }
      setDeleteGroup(null);
      showFeedback(
        "success",
        data.message || "Rol eliminado correctamente."
      );
      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo eliminar el rol."
      );
    } finally {
      setDeletingId(null);
    }
  };
  const formatDate = (value) => {
    if (!value) {
      return "Sin fecha";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "Sin fecha";
    }
    return new Intl.DateTimeFormat("es-EC", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  };
  if (loading && !organization) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        organization={organization}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          padding: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
              justifyContent: "space-between",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 2,
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                gap: 1,
              }}
            >
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{
                  display: {
                    xs: "inline-flex",
                    md: "none",
                  },
                }}
              >
                <MenuRoundedIcon />
              </IconButton>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  sx={{
                    color: "#111111",
                    fontSize: {
                      xs: 26,
                      md: 32,
                    },
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  Roles
                </Typography>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 14,
                    fontWeight: 500,
                    marginTop: 0.5,
                  }}
                >
                  {groups.length} roles registrados
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={openCreate}
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
                minHeight: 44,
                borderBottom: "3px solid #b00020",
              }}
            >
              Nuevo rol
            </Button>
          </Box>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              border: "1px solid #e1e1e4",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                padding: {
                  xs: 2,
                  sm: 2.5,
                },
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder="Buscar por nombre o descripción"
              />
            </Box>
            <Box
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Rol</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Creación</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedGroups.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Box
                            sx={{
                              minHeight: 220,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography sx={{ fontWeight: 800 }}>
                              No hay roles
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedGroups.map((group) => (
                        <TableRow key={group.id} hover>
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 800,
                              }}
                            >
                              {group.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {group.description || "Sin descripción"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={group.is_active ? "Activo" : "Inactivo"}
                              sx={{
                                fontWeight: 700,
                                color: group.is_active ? "#166534" : "#b91c1c",
                                backgroundColor: group.is_active ? "#f0fdf4" : "#fef2f2",
                                border: group.is_active ? "1px solid #bbf7d0" : "1px solid #fecaca",
                              }}
                            />
                          </TableCell>
                          <TableCell>{formatDate(group.created_at)}</TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setDetailGroup(group)}
                              >
                                Ver
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => openEdit(group)}
                              >
                                Editar
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={updatingId === group.id}
                                onClick={() => handleToggleStatus(group)}
                              >
                                {updatingId === group.id
                                  ? "Procesando"
                                  : group.is_active
                                    ? "Desactivar"
                                    : "Activar"}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => setDeleteGroup(group)}
                              >
                                Eliminar
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            <Box
              sx={{
                display: {
                  xs: "block",
                  md: "none",
                },
                padding: 2,
              }}
            >
              {paginatedGroups.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontWeight: 800 }}>
                    No hay roles
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {paginatedGroups.map((group) => (
                    <Box
                      key={group.id}
                      sx={{
                        padding: 2,
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                          marginBottom: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 800,
                            wordBreak: "break-word",
                          }}
                        >
                          {group.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={group.is_active ? "Activo" : "Inactivo"}
                          sx={{
                            flexShrink: 0,
                            fontWeight: 700,
                            color: group.is_active ? "#166534" : "#b91c1c",
                            backgroundColor: group.is_active ? "#f0fdf4" : "#fef2f2",
                            border: group.is_active ? "1px solid #bbf7d0" : "1px solid #fecaca",
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          color: "#6b7280",
                          fontSize: 13,
                          lineHeight: 1.5,
                          marginBottom: 1.5,
                        }}
                      >
                        {group.description || "Sin descripción"}
                      </Typography>
                      <Typography sx={{ fontSize: 13, marginBottom: 1.5 }}>
                        <strong>Creación:</strong>{" "}
                        {formatDate(group.created_at)}
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 1,
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setDetailGroup(group)}
                        >
                          Ver
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openEdit(group)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={updatingId === group.id}
                          onClick={() => handleToggleStatus(group)}
                        >
                          {updatingId === group.id
                            ? "Procesando"
                            : group.is_active
                              ? "Desactivar"
                              : "Activar"}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => setDeleteGroup(group)}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
            <TablePagination
              component="div"
              count={filteredGroups.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Filas"
              sx={{
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                },
              }}
            />
          </Paper>
        </Box>
      </Box>
      <Dialog
        open={formOpen}
        onClose={closeForm}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {form.id ? "Editar rol" : "Nuevo rol"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ paddingTop: 1 }}>
            <TextField
              fullWidth
              required
              name="name"
              label="Nombre del rol"
              value={form.name}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  name: event.target.value,
                }))
              }
            />
            <TextField
              fullWidth
              multiline
              minRows={4}
              name="description"
              label="Descripción"
              value={form.description}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  description: event.target.value,
                }))
              }
            />
            {form.id && (
              <Box
                sx={{
                  minHeight: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  paddingX: 1.5,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  Rol activo
                </Typography>
                <Switch
                  checked={form.is_active}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      is_active: event.target.checked,
                    }))
                  }
                />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : form.id ? (
              "Guardar cambios"
            ) : (
              "Crear rol"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(detailGroup)}
        onClose={() => setDetailGroup(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Información del rol</DialogTitle>
        <DialogContent>
          {detailGroup && (
            <Stack spacing={1.5} sx={{ paddingTop: 1 }}>
              {[
                ["Nombre", detailGroup.name],
                [
                  "Descripción",
                  detailGroup.description || "Sin descripción",
                ],
                ["Estado", detailGroup.is_active ? "Activo" : "Inactivo"],
                ["Fecha de creación", formatDate(detailGroup.created_at)],
                [
                  "Última actualización",
                  formatDate(detailGroup.updated_at),
                ],
              ].map(([label, value]) => (
                <Box
                  key={label}
                  sx={{
                    paddingBottom: 1.2,
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      marginTop: 0.3,
                      wordBreak: "break-word",
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailGroup(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(deleteGroup)}
        onClose={() => {
          if (!deletingId) {
            setDeleteGroup(null);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar rol</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Deseas eliminar el rol {deleteGroup?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteGroup(null)}
            disabled={Boolean(deletingId)}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={Boolean(deletingId)}
          >
            {deletingId ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : (
              "Eliminar"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={feedback.open} autoHideDuration={3500} onClose={() => setFeedback((previous) => ({...previous,open:false}))} anchorOrigin={{vertical:"top",horizontal:"right"}} sx={{marginTop:1}}>
        <Alert severity={feedback.severity} variant="outlined" onClose={() => setFeedback((previous) => ({...previous,open:false}))} sx={{minWidth:{xs:"calc(100vw - 32px)",sm:340},maxWidth:460,borderRadius:2,boxShadow:"0 8px 24px rgba(0,0,0,0.08)",fontWeight:700,backgroundColor:feedback.severity==="success"?"#f0fdf4":"#fef2f2",borderColor:feedback.severity==="success"?"#bbf7d0":"#fecaca",color:feedback.severity==="success"?"#166534":"#b91c1c","& .MuiAlert-icon":{color:feedback.severity==="success"?"#16a34a":"#dc2626"},"& .MuiAlert-action":{color:feedback.severity==="success"?"#166534":"#b91c1c"}}}>
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}